import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  Input,
  AfterContentInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { auditTime, distinctUntilChanged } from 'rxjs/operators';
import { DateRange } from '@app/shared/models/shared.model';
import { MY_FORMATS } from '@app/app.constants';
import { DateFnsAdapter } from '@angular/material-date-fns-adapter';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import enUS from 'date-fns/locale/en-US';

@Component({
  selector: 'app-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: DateFnsAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_LOCALE, useValue: enUS },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class DateRangePickerComponent
  implements OnInit, AfterContentInit, OnChanges
{
  range: FormGroup;
  @Input() opened = false;
  @Input() disabled = false;
  @Input() disableClear = false;
  @Input() isRequired = false;
  @Input() defaultDate: DateRange;
  @Input() newDateRange: DateRange;
  @Input() maxDate?: Date;
  @Input() minDate?: Date;
  /**
   * This is used to determine if the date range picker is part of a filter or form selection as this is required
   * different style layout for the date range picker because of the limited space in the filter section
   * If it is part of a filter, then isPartOfFilter will be true
   * If it is part of a form selection or any other, then isPartOfFilter will be false
   */
  @Input() isPartOfFilter?: boolean;
  @Input() errorStyleHandleRequired?: boolean;
  @Input() removeBorder?: boolean;
  @Input() errorBorder?: boolean;
  @ViewChild('datepickerFooter', { static: false })
  datepickerFooter: ElementRef;
  @ViewChild('picker', { static: false }) datepicker: MatDatepicker<any>;
  @Output() dateRangeChange = new EventEmitter<{ dateRange: DateRange }>();
  constructor(private formBuilder: FormBuilder) {
    this.range = this.formBuilder.group({
      start: [''],
      end: [''],
    });
  }

  ngOnInit() {
    this.range.valueChanges
      .pipe(
        auditTime(0), //https://github.com/angular/components/issues/19776 to fix the issue of multiple events being fired
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe((value) => {
        this.dateRangeChange.emit({ dateRange: value });
      });
  }

  ngAfterContentInit() {
    if (this.defaultDate?.start && this.defaultDate?.end) {
      this.range.setValue({
        start: this.defaultDate.start,
        end: this.defaultDate.end,
      });
    }
    if (this.disabled) {
      this.range.disable();
    }
  }
  ngOnChanges(changes: SimpleChanges): void {
    const newDateRange = changes?.newDateRange?.currentValue;
    this.newDateRange = newDateRange;

    if (this.newDateRange?.start && this.newDateRange?.end) {
      this.range.setValue({
        start: this.newDateRange.start,
        end: this.newDateRange.end,
      });
    }
  }

  onBlur() {
    this.requireBothInputs();
  }

  requireBothInputs() {
    if (this.startFormControl.value && !this.endFormControl.value) {
      this.endFormControl.addValidators(Validators.required);
      this.endFormControl.updateValueAndValidity();
    } else if (this.endFormControl.value && !this.startFormControl.value) {
      this.startFormControl.addValidators(Validators.required);
      this.startFormControl.updateValueAndValidity();
    } else if (
      !this.startFormControl.value &&
      !this.endFormControl.value &&
      this.isRequired
    ) {
      this.startFormControl.addValidators(Validators.required);
      this.endFormControl.addValidators(Validators.required);
      this.startFormControl.updateValueAndValidity();
      this.endFormControl.updateValueAndValidity();
    } else {
      this.endFormControl.clearValidators();
      this.startFormControl.clearValidators();
      this.startFormControl.updateValueAndValidity();
      this.endFormControl.updateValueAndValidity();
    }
  }

  clear() {
    this.range.setValue({
      start: '',
      end: '',
    });
    this.requireBothInputs();
    this.datepicker.close();
  }

  onOpen() {
    if (!this.disableClear) {
      this.appendFooter();
    }
  }
  onClose() {
    this.requireBothInputs();
  }

  private appendFooter() {
    const matCalendar = document.getElementsByClassName(
      'mat-datepicker-content'
    )[0] as HTMLElement;
    matCalendar.appendChild(this.datepickerFooter.nativeElement);
  }

  datePickerValid(type: string): boolean {
    if (
      type === 'start' &&
      this.range.dirty &&
      this.startFormControl.invalid &&
      ((!this.startFormControl.value && this.endFormControl.value) ||
        this.startFormControl.invalid)
    ) {
      return false;
    }
    if (
      type === 'end' &&
      this.range.dirty &&
      ((this.startFormControl.value && !this.endFormControl.value) ||
        this.endFormControl.invalid)
    ) {
      return false;
    }
    return true;
  }

  datePickerEndBeforeStart() {
    return (
      Date.parse(this.endFormControl.value) <
      Date.parse(this.startFormControl.value)
    );
  }

  datePickerInputBlur() {
    if (!this.startFormControl.value && !this.endFormControl.value) {
      this.startFormControl.markAsUntouched();
      this.endFormControl.markAsUntouched();
    }
  }
  get startFormControl() {
    return this.range.controls.start;
  }
  get endFormControl() {
    return this.range.controls.end;
  }
  get dateRangeSelected(): boolean {
    return this.range.value.start && this.range.value.end;
  }
}
